import { toaster } from '../components/ui/toaster';

export const parseCookies = (cookieString: string) => {
  return cookieString.split(';').reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>
  );
};

export const requireNonEmptyTrimmed = (raw: string, label: string): string | null => {
  const str = raw.trim();
  if (str === '' || str === undefined || str === null) {
    toaster.create({
      title: 'Required field',
      description: `${label} is required.`,
      type: 'error',
    });
    return null;
  }
  return str;
};

export const MIN_PASSWORD_LENGTH = parseInt(import.meta.env.MIN_PASSWORD_LENGTH ?? '8', 10) || 8;
/** At least one lowercase, uppercase, digit, and symbol; length enforced separately. */
export const MIN_PASSWORD_COMPLEXITY =
  import.meta.env.MIN_PASSWORD_COMPLEXITY || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;

export function validatePasswordShape(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (!MIN_PASSWORD_COMPLEXITY.test(password)) {
    return 'Password must include uppercase, lowercase, a number, and a symbol.';
  }
  return null;
}

/** Strip trailing zeros after fixed decimal formatting (e.g. "220.00" → "220"). */
function formatDisplayValue(value: number, decimalPlaces: number): string {
  return value.toFixed(decimalPlaces).replace(/\.?0+$/, '') || '0';
}

export function parseToUserWeightUnit(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '0';
  }
  const unit = localStorage.getItem('weight-unit') || 'kg';
  const converted = unit === 'lb' ? value * 2.2046226218 : value;
  return formatDisplayValue(converted, 2);
}

/** Operating / MTBF durations from millisecond counters. */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) {
    return '—';
  }
  const hours = ms / 3_600_000;
  if (hours >= 1) {
    const rounded = hours >= 10 ? hours.toFixed(0) : hours.toFixed(1);
    return `${rounded.replace(/\.0$/, '')} h`;
  }
  const minutes = ms / 60_000;
  if (minutes >= 1) {
    return `${Math.round(minutes)} min`;
  }
  return `${Math.max(0, Math.round(ms / 1000))} s`;
}

export function parseToUserLengthUnit(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '0';
  }
  const unit = localStorage.getItem('length-unit') || 'mm';
  const converted = unit === 'in' ? value / 25.4 : value;
  return formatDisplayValue(converted, unit === 'in' ? 2 : 0);
}
